/* eslint-disable no-unused-vars */
import { observer } from "mobx-react-lite";
import { useStore } from "../../../root-store-context";
import * as React from "react";
import { Button, Checkbox, Select, Table, TableColumnsType } from "antd";
import { MailJobFile } from "../../../stores/AppStore/ApplicationStore";
import { RcFile } from "antd/es/upload";
import { DataTypeTableUpload } from "./UploadApp";
import { useCamIsMobile } from "hooks/useCamIsMobile";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement> & { "data-row-key": string }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: props["data-row-key"] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && (child as any).props.className?.includes("drag-handle-cell")) {
          return React.cloneElement(child as React.ReactElement<any>, {
            children: (
              <HolderOutlined
                ref={setActivatorNodeRef}
                style={{ cursor: isDragging ? "grabbing" : "grab", touchAction: "none", fontSize: 16, color: "#94a3b8" }}
                {...attributes}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

export const UploadGeneralList = observer(() => {
  const { applicationStore, modalViewerStore } = useStore();
  const isMobile = useCamIsMobile();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const openViewFile = (file: RcFile) => {
    const blob = new Blob([file], { type: "application/pdf" });
    const fileURL = URL.createObjectURL(blob);
    modalViewerStore.openFileByUrl(fileURL, "Recipient Info.pdf");
  };

  const handleCardSelect = (checked: boolean, keyData: string) => {
    const current = applicationStore.selectedFiles;
    if (checked) {
      applicationStore.setSelectedFiles([...current, keyData]);
    } else {
      applicationStore.setSelectedFiles(current.filter((k) => k !== keyData));
    }
  };

  const renderSelectedCommunity = (record: DataTypeTableUpload): React.ReactNode => (
    <Select
      data-testid={`cam-general-community-select-${record.keyData}`}
      showSearch
      style={{ width: "100%" }}
      value={record.fileInfo.communityKey}
      placeholder="Search to Property"
      optionFilterProp="children"
      filterOption={(input, option) =>
        (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
      }
      filterSort={(optionA, optionB) =>
        (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
      }
      onChange={(value) => {
        applicationStore.setSelectedFileData(record.keyData.toString(), { communityKey: value });
        applicationStore.clearGeneralFormData();
      }}
      options={applicationStore.communities?.map((_) => ({ label: _.titleInDropdown, value: _.id })) || []}
    />
  );

  const getDataTypeGeneral = (_: MailJobFile): DataTypeTableUpload => ({
    key: _.guid,
    keyData: _.guid,
    fileName: _.name,
    file: _.file,
    fileInfo: _.fileInfo,
  });

  const data = applicationStore.mailJob.files?.map((_) => getDataTypeGeneral(_)) || [];

  const allSelected = data.length > 0 && data.every((r) =>
    applicationStore.selectedFiles.includes(r.keyData.toString())
  );
  const someSelected = data.some((r) =>
    applicationStore.selectedFiles.includes(r.keyData.toString())
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const fromIndex = data.findIndex((d) => d.keyData === active.id);
    const toIndex = data.findIndex((d) => d.keyData === over.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      applicationStore.reorderFiles(fromIndex, toIndex);
    }
  };

  /* ════════════════════════════════════
     MOBILE
  ════════════════════════════════════ */
  if (isMobile) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={data.map((d) => d.keyData.toString())} strategy={verticalListSortingStrategy}>
          <div className="cam__upload-cards">
            {data.map((record, index) => {
              const isSelected = applicationStore.selectedFiles.includes(record.keyData.toString());
              return (
                <SortableMobileCard
                  key={record.key.toString()}
                  record={record}
                  index={index}
                  isSelected={isSelected}
                  onSelect={handleCardSelect}
                  onOpenFile={openViewFile}
                >
                  <div className="cam__upload-card-fields">
                    <div className="cam__upload-card-field">
                      <label className="cam__upload-card-label">Property</label>
                      {renderSelectedCommunity(record)}
                    </div>
                  </div>
                </SortableMobileCard>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  /* ════════════════════════════════════
     DESKTOP
  ════════════════════════════════════ */
  const columns: TableColumnsType<DataTypeTableUpload> = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                applicationStore.setSelectedFiles(data.map((r) => r.keyData.toString()));
              } else {
                applicationStore.setSelectedFiles([]);
              }
            }}
          />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Select All</span>
        </div>
      ),
      dataIndex: "select",
      width: 90,
      align: "center" as const,
      render: (_: any, record: DataTypeTableUpload) => (
        <Checkbox
          checked={applicationStore.selectedFiles.includes(record.keyData.toString())}
          onChange={(e) => handleCardSelect(e.target.checked, record.keyData.toString())}
        />
      ),
    },
    {
      title: "",
      dataIndex: "drag",
      width: 40,
      className: "drag-handle-cell",
      render: () => null,
    },
    {
      title: "Order",
      dataIndex: "order",
      width: 60,
      render: (_: any, __: DataTypeTableUpload, index: number) => (
        <span style={{ fontWeight: 700, color: "#2563eb" }}>{index + 1}</span>
      ),
    },
    {
      title: "File",
      dataIndex: "fileName",
      width: 200,
      ellipsis: true,
      render: (text: string, record: DataTypeTableUpload) => (
        <Button type="link" onClick={() => openViewFile(record.file)}>
          {text}
        </Button>
      ),
    },
    {
      title: "Property",
      dataIndex: "fileInfo",
      width: 300,
      ellipsis: true,
      render: (text: string, record: DataTypeTableUpload) => renderSelectedCommunity(record),
    },
  ];

  const columnsAligned = columns.map((col) => ({
    ...col,
    onCell: () => ({ style: { verticalAlign: "middle" as const } }),
    onHeaderCell: () => ({ style: { verticalAlign: "middle" as const } }),
  }));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={data.map((d) => d.keyData.toString())} strategy={verticalListSortingStrategy}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <Table
            pagination={false}
            columns={columnsAligned}
            dataSource={data}
            scroll={{ x: 690 }}
            style={{ tableLayout: "fixed" }}
            components={{ body: { row: SortableRow } }}
          />
        </div>
      </SortableContext>
    </DndContext>
  );
});

const SortableMobileCard = ({
  record,
  index,
  isSelected,
  onSelect,
  onOpenFile,
  children,
}: {
  record: DataTypeTableUpload;
  index: number;
  isSelected: boolean;
  onSelect: (checked: boolean, keyData: string) => void;
  onOpenFile: (file: RcFile) => void;
  children?: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: record.keyData.toString() });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cam__upload-card ${isSelected ? "cam__upload-card--selected" : ""}`}
    >
      <div className="cam__upload-card-header">
        <Checkbox
          checked={isSelected}
          aria-label={`Select file ${record.fileName}`}
          onChange={(e) => onSelect(e.target.checked, record.keyData.toString())}
        />
        <span style={{ fontWeight: 700, color: "#2563eb", flexShrink: 0 }}>
          #{index + 1}
        </span>
        <Button
          type="link"
          className="cam__upload-card-filename"
          onClick={() => onOpenFile(record.file)}
        >
          {record.fileName}
        </Button>
        <HolderOutlined
          ref={setActivatorNodeRef}
          style={{ cursor: isDragging ? "grabbing" : "grab", touchAction: "none", fontSize: 18, color: "#94a3b8", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44 }}
          {...attributes}
          {...listeners}
        />
      </div>
      {children}
    </div>
  );
};
